import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import axios from "axios";
import "./RDScheme.css";

const API_BASE = "https://payyug.in/api";

const CREATE_API = `${API_BASE}/create-rddd`;
const LIST_API = `${API_BASE}/get-all-rddd`;
const UPDATE_API = `${API_BASE}/update`;
const DELETE_API = `${API_BASE}/delete-rddd`;

const initialForm = {
  schemeType: "",
  serviceName: "",
  recurringFrequency: "",
  minTenure: "",
  minDepositInstallment: "",
  additionalBonus: "",
  depositLockinPeriod: "",
  interestLockinPeriod: "",
  preClosureRoi: "",
  closureChargeBeforeType: "Fixed",
  closureChargeBeforeValue: "",
  closureChargeAfterType: "Fixed",
  closureChargeAfterValue: "",
  installmentPenaltyType: "Fixed",
  installmentPenaltyValue: "",
  gracePeriod: "",
  roi: "",
  roiFrequency: "",
  interestCompounding: "Simple Interest",
  prefix: "",
  schemeStatus: "Active",
  isFixedTenure: false,
  maxTenure: "",
  superAdminId: "",
};

const UpdatedRDSchemeFormWithAPI = () => {
  const [formData, setFormData] = useState(initialForm);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);

  const fetchSchemes = async () => {
    const adminId = localStorage.getItem("adminuserId");
    setLoading(true);
    try {
      const res = await axios.get(`${LIST_API}?superAdminId=${adminId}`);
      const data = res.data?.data ?? res.data;
      setSchemes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch schemes error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const adminId = localStorage.getItem("adminuserId");
    if (adminId) {
      setFormData((prev) => ({ ...prev, superAdminId: adminId }));
    }
    fetchSchemes();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const startEdit = (scheme) => {
    const adminId = localStorage.getItem("adminuserId") || "";
    setEditingId(scheme.id ?? scheme._id ?? null);
    setFormData({
      ...initialForm,
      ...scheme,
      superAdminId: adminId,
      isFixedTenure: !!scheme.isFixedTenure,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearForm = () => {
    const adminId = localStorage.getItem("adminuserId") || "";
    setFormData({ ...initialForm, superAdminId: adminId });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const adminId = localStorage.getItem("adminuserId");
      const finalData = {
        ...formData,
        superAdminId: adminId,
        minTenure: Number(formData.minTenure) || 0,
        minDepositInstallment: Number(formData.minDepositInstallment) || 0,
        additionalBonus: Number(formData.additionalBonus) || 0,
        depositLockinPeriod: Number(formData.depositLockinPeriod) || 0,
        interestLockinPeriod: Number(formData.interestLockinPeriod) || 0,
        preClosureRoi: Number(formData.preClosureRoi) || 0,
        closureChargeBeforeValue: Number(formData.closureChargeBeforeValue) || 0,
        closureChargeAfterValue: Number(formData.closureChargeAfterValue) || 0,
        installmentPenaltyValue: Number(formData.installmentPenaltyValue) || 0,
        gracePeriod: Number(formData.gracePeriod) || 0,
        roi: Number(formData.roi) || 0,
        maxTenure: Number(formData.maxTenure) || 0,
        isFixedTenure: formData.isFixedTenure ? 1 : 0,
      };

      if (editingId) {
        await axios.put(`${UPDATE_API}/${editingId}`, finalData);
        alert("RD/DD Scheme updated successfully!");
      } else {
        await axios.post(CREATE_API, finalData);
        alert("RD/DD Scheme created successfully!");
      }

      await fetchSchemes();
      clearForm();
    } catch (err) {
      console.error("Submit error:", err);
      setError("Submit failed. Check API response.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this scheme?")) return;
    console.log(id)
    setSubmitting(true);
    try {
      await axios.delete(`${DELETE_API}/${id}`);
      await fetchSchemes();
      alert("Deleted successfully");
    } catch (err) {
      console.error("Delete error:", err);
      setError("Delete failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = schemes.filter((s) => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      String(s.serviceName ?? "").toLowerCase().includes(q) ||
      String(s.schemeType ?? "").toLowerCase().includes(q) ||
      String(s.prefix ?? "").toLowerCase().includes(q)
    );
  });

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="rddd-main-container">
      <div className="rddd-form-container">
        <h2>{editingId ? "Edit Recurring Deposit Scheme" : "Recurring Deposit Scheme"}</h2>
        {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Row 1 */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>Recurring Deposit Type</label>
              <select name="schemeType" value={formData.schemeType} onChange={handleChange} required>
                <option value="">Select Type</option>
                <option value="RD Account">RD Account</option>
                <option value="DD Account">DD Account</option>
              </select>
            </div>

            <div className="rdddform-group">
              <label>Service Name</label>
              <input
                type="text"
                name="serviceName"
                placeholder="Enter service name"
                value={formData.serviceName}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>Recurring Frequency</label>
              <div className="rdddinline-input-row">
                <select
                  name="recurringFrequency"
                  value={formData.recurringFrequency}
                  onChange={handleChange}
                >
                  <option value="">Select Frequency</option>
                  <option value="Daily">Daily (For DD Only)</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
                <input
                  type="number"
                  name="minTenure"
                  placeholder="Min Tenure (Months)"
                  value={formData.minTenure}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="rdddform-group">
              <label>Minimum Deposit Installment</label>
              <input
                type="number"
                name="minDepositInstallment"
                placeholder="Enter minimum deposit installment"
                value={formData.minDepositInstallment}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>Additional Bonus (%)</label>
              <input
                type="number"
                name="additionalBonus"
                placeholder="Enter additional bonus"
                value={formData.additionalBonus}
                onChange={handleChange}
              />
            </div>

            <div className="rdddform-group">
              <label>Deposit Lockin Period (Months)</label>
              <input
                type="number"
                name="depositLockinPeriod"
                placeholder="Enter lockin period"
                value={formData.depositLockinPeriod}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>Interest Lockin Period</label>
              <input
                type="number"
                name="interestLockinPeriod"
                placeholder="Enter interest lockin period"
                value={formData.interestLockinPeriod}
                onChange={handleChange}
              />
            </div>

            <div className="rdddform-group">
              <label>Pre-Closure ROI (%)</label>
              <input
                type="number"
                name="preClosureRoi"
                placeholder="Enter pre-closure ROI"
                value={formData.preClosureRoi}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Row 5 - Closure Charge */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>Closure Charge Before</label>
              <div className="rdddinline-input-row">
                <select
                  name="closureChargeBeforeType"
                  value={formData.closureChargeBeforeType}
                  onChange={handleChange}
                >
                  <option value="Fixed">Fixed</option>
                  <option value="Percentage">Percentage</option>
                </select>
                <input
                  type="number"
                  name="closureChargeBeforeValue"
                  placeholder="Value"
                  value={formData.closureChargeBeforeValue}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="rdddform-group">
              <label>Closure Charge After</label>
              <div className="rdddinline-input-row">
                <select
                  name="closureChargeAfterType"
                  value={formData.closureChargeAfterType}
                  onChange={handleChange}
                >
                  <option value="Fixed">Fixed</option>
                  <option value="Percentage">Percentage</option>
                </select>
                <input
                  type="number"
                  name="closureChargeAfterValue"
                  placeholder="Value"
                  value={formData.closureChargeAfterValue}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Row 6 */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>Installment Penalty</label>
              <div className="rdddinline-input-row">
                <select
                  name="installmentPenaltyType"
                  value={formData.installmentPenaltyType}
                  onChange={handleChange}
                >
                  <option value="Fixed">Fixed</option>
                  <option value="Percentage">Percentage</option>
                </select>
                <input
                  type="number"
                  name="installmentPenaltyValue"
                  placeholder="Enter penalty"
                  value={formData.installmentPenaltyValue}
                  onChange={handleChange}
                />
                <input
                  type="number"
                  name="gracePeriod"
                  placeholder="Grace Period (days)"
                  value={formData.gracePeriod}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="rdddform-group">
              <label>ROI (%)</label>
              <input
                type="number"
                name="roi"
                placeholder="Enter ROI"
                value={formData.roi}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Row 7 */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>ROI Frequency</label>
              <select
                name="roiFrequency"
                value={formData.roiFrequency}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="M">Monthly</option>
                <option value="Q">Quarterly</option>
                <option value="H">Half-Yearly</option>
                <option value="Y">Yearly</option>
              </select>
            </div>

            <div className="rdddform-group">
              <label>Interest Compounding</label>
              <select
                name="interestCompounding"
                value={formData.interestCompounding}
                onChange={handleChange}
              >
                <option value="Simple Interest">Simple Interest</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Half-Yearly">Half-Yearly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>
          </div>

          {/* Row 8 */}
          <div className="rdddform-row">
            <div className="rdddform-group">
              <label>Prefix</label>
              <input
                type="text"
                name="prefix"
                placeholder="Enter prefix"
                value={formData.prefix}
                onChange={handleChange}
              />
            </div>

            <div className="rdddform-group">
              <label>Is Fixed Tenure?</label>
              <div
                className={`rdddpill-toggle ${formData.isFixedTenure ? "active" : ""}`}
                onClick={() =>
                  setFormData({ ...formData, isFixedTenure: !formData.isFixedTenure })
                }
              >
                <div className="rdddpill-indicator"></div>
                <span className="rdddpill-label">Yes</span>
                <span className="rdddpill-label">No</span>
              </div>
            </div>
          </div>

          {formData.isFixedTenure && (
            <div className="rdddform-row">
              <div className="rdddform-group">
                <label>Fixed / Max Duration (months)</label>
                <input
                  type="number"
                  name="maxTenure"
                  placeholder="Enter duration"
                  value={formData.maxTenure}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="rd-scheme-btn">
            <button type="submit" className="rdddsubmit-btn" disabled={submitting}>
              {submitting
                ? editingId
                  ? "Updating..."
                  : "Submitting..."
                : editingId
                ? "Update"
                : "Submit"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={clearForm}
                  style={{ marginLeft: 8, background: "#e46a6a" }}
                className="rdddsubmit-btn"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Report */}
      <div className="rddd-report-container">
        <h2>Recurring Deposit Scheme Report</h2>
        <div className="rdddreport-controls">
          <div className="rddd-search">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search scheme..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="rddd-entire-page">
            <label>Entries per page:</label>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>

        <div className="rddd-report-table-container">
          <table className="rddd-report-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Scheme</th>
                <th>Prefix</th>
                <th>ROI</th>
                <th>Min Installment</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              ) : (
                paginated.map((s, idx) => (
                  <tr key={s.id ?? idx}>
                    <td>{(page - 1) * perPage + idx + 1}</td>
                    <td>{s.schemeType||"N/A"}</td>
                    <td>{s.serviceName||"N/A"}</td>
                    <td>{s.prefix||"N/A"}</td>
                    <td>{s.roi||"N/A"}</td>
                    <td>{s.minDepositInstallment||"N/A"}</td>
                    <td>
                      <div className="rd-scheme-action-btns">
                        <button onClick={() => startEdit(s)} aria-label="Edit">
                          <FaEdit />
                        </button>

                        <button onClick={() => handleDelete(s.id)} aria-label="Delete">
                          <FaTrash  />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UpdatedRDSchemeFormWithAPI;
